/**
 * sessionService.js
 *
 * Manages per-browser session records stored under users/{uid}/sessions/{sessionId}.
 * Works alongside Firebase Authentication — does NOT replace it.
 *
 * Session document shape:
 * {
 *   sessionId       : string
 *   userId          : string   (UID — set once by trackSession, never changed)
 *   browser         : string   (e.g. "Chrome")
 *   operatingSystem : string   (e.g. "Windows")
 *   deviceType      : string   ("Desktop" | "Mobile" | "Tablet")
 *   deviceName      : string   (e.g. "Chrome on Windows")
 *   createdAt       : Timestamp
 *   lastActiveAt    : Timestamp
 * }
 *
 * Revocation = document deletion.
 * When a session is revoked (individually or via logoutAllSessions), the
 * Cloud Function deletes the document. The onSnapshot listener detects the
 * deletion via !snap.exists() and triggers sign-out on that device.
 * There are no `revoked` / `revokedAt` fields — deleted means revoked.
 *
 * The current browser's sessionId is persisted in sessionStorage so it
 * survives page refreshes but is cleared when the tab/window is closed.
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SESSION_ID_KEY = 'currentSessionId';

/**
 * Minimum ms between automatic lastActiveAt writes.
 * A write only happens when this interval has elapsed since the last write.
 */
const ACTIVITY_THROTTLE_MS = 10 * 60 * 1000; // 10 minutes

// Track the last time we wrote an activity update (in-memory, per tab)
let _lastActivityWrite = 0;

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function detectBrowser(ua) {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\/|Opera/.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  return 'Unknown Browser';
}

function detectOS(ua) {
  if (/Windows/.test(ua)) return 'Windows';
  if (/Android/.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown OS';
}

function detectDeviceType(ua) {
  if (/iPad/.test(ua)) return 'Tablet';
  if (/Android/.test(ua) && !/Mobile/.test(ua)) return 'Tablet';
  if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/.test(ua))
    return 'Mobile';
  return 'Desktop';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const sessionService = {
  /**
   * Create a new session document in Firestore and store the sessionId in
   * sessionStorage so the current browser can identify itself across refreshes.
   *
   * Called once per login (after Firebase Auth sign-in succeeds).
   * @param {string} uid — authenticated Firebase UID
   * @returns {Promise<string|null>} the generated sessionId, or null on error
   */
  trackSession: async (uid) => {
    if (!uid) return null;
    try {
      const sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const ua = navigator.userAgent;

      const browser = detectBrowser(ua);
      const operatingSystem = detectOS(ua);
      const deviceType = detectDeviceType(ua);
      const deviceName = `${browser} on ${operatingSystem}`;

      const sessionData = {
        sessionId,
        userId: uid,
        browser,
        operatingSystem,
        deviceType,
        deviceName,
        createdAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'users', uid, 'sessions', sessionId), sessionData);

      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
      _lastActivityWrite = Date.now();

      return sessionId;
    } catch (err) {
      console.error('[sessionService] trackSession error:', err);
      return null;
    }
  },

  /**
   * Return the sessionId for the current browser tab, or null if not set.
   */
  getCurrentSessionId: () => sessionStorage.getItem(SESSION_ID_KEY),

  /**
   * Throttled lastActiveAt update. Writes to Firestore at most once per
   * ACTIVITY_THROTTLE_MS (10 min). Safe to call on user interactions.
   * @param {string} uid
   */
  updateSessionActivity: async (uid) => {
    if (!uid) return;
    const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) return;

    const now = Date.now();
    if (now - _lastActivityWrite < ACTIVITY_THROTTLE_MS) return;

    try {
      await updateDoc(
        doc(db, 'users', uid, 'sessions', sessionId),
        { lastActiveAt: serverTimestamp() }
      );
      _lastActivityWrite = now;
    } catch (err) {
      console.warn('[sessionService] updateSessionActivity error:', err);
    }
  },

  /**
   * Subscribe to the current browser's session document.
   * Calls `onRevoked` when the document is deleted (= session revoked).
   * Deletion can happen via revokeSession or logoutAllSessions Cloud Functions.
   *
   * Returns the unsubscribe function.
   * @param {string} uid
   * @param {() => void} onRevoked — called when the session is deleted
   */
  watchCurrentSession: (uid, onRevoked) => {
    if (!uid) return () => {};
    const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) return () => {};

    const ref = doc(db, 'users', uid, 'sessions', sessionId);
    return onSnapshot(
      ref,
      (snap) => {
        // Document deleted = session was revoked
        if (!snap.exists()) {
          onRevoked();
        }
      },
      (err) => {
        console.warn('[sessionService] watchCurrentSession error:', err);
      }
    );
  },

  /**
   * Subscribe to all session documents for a user with a real-time listener.
   * Every document in the collection is an active session — no filtering needed.
   * Returns an unsubscribe function.
   * @param {string} uid
   * @param {(sessions: Array) => void} onChange
   */
  watchSessions: (uid, onChange) => {
    if (!uid) return () => {};
    const ref = collection(db, 'users', uid, 'sessions');
    return onSnapshot(
      ref,
      (snapshot) => {
        const currentId = sessionStorage.getItem(SESSION_ID_KEY);
        const sessions = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort: current session first, then by lastActiveAt desc
        sessions.sort((a, b) => {
          if (a.sessionId === currentId) return -1;
          if (b.sessionId === currentId) return 1;
          const aTime = a.lastActiveAt?.toMillis?.() ?? 0;
          const bTime = b.lastActiveAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });
        onChange(sessions);
      },
      (err) => {
        console.warn('[sessionService] watchSessions error:', err);
      }
    );
  },

  /**
   * Fetch all session documents for a user (one-time read).
   * Every document present is an active session.
   * @param {string} uid
   * @returns {Promise<Array>}
   */
  fetchActiveSessions: async (uid) => {
    if (!uid) return [];
    try {
      const snapshot = await getDocs(collection(db, 'users', uid, 'sessions'));
      const currentId = sessionStorage.getItem(SESSION_ID_KEY);
      const sessions = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      sessions.sort((a, b) => {
        if (a.sessionId === currentId) return -1;
        if (b.sessionId === currentId) return 1;
        const aTime = a.lastActiveAt?.toMillis?.() ?? 0;
        const bTime = b.lastActiveAt?.toMillis?.() ?? 0;
        return bTime - aTime;
      });
      return sessions;
    } catch (err) {
      console.error('[sessionService] fetchActiveSessions error:', err);
      return [];
    }
  },

  /**
   * Delete the current browser's own session document directly from Firestore.
   * Use this on self-logout — the user is still authenticated so the Firestore
   * rules allow the delete. Faster and more reliable than going through the
   * Cloud Function for self-revocation.
   * @param {string} uid
   * @returns {Promise<void>}
   */
  deleteCurrentSession: async (uid) => {
    if (!uid) return;
    try {
      const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
      if (!sessionId) return;
      await deleteDoc(doc(db, 'users', uid, 'sessions', sessionId));
    } catch (err) {
      console.warn('[sessionService] deleteCurrentSession error:', err);
    }
  },

  /**
   * Clear the current session ID from sessionStorage on logout.
   * The Firestore document is deleted by the revokeSession Cloud Function.
   */
  clearCurrentSession: () => {
    sessionStorage.removeItem(SESSION_ID_KEY);
    _lastActivityWrite = 0;
  },

  /**
   * No-op — kept for call-site compatibility.
   * Sessions are now deleted on revocation so there are no stale revoked
   * documents to prune. This function does nothing and can be safely called.
   */
  pruneOldSessions: async (_uid, _maxAgeDays) => {
    // Intentional no-op: revocation = deletion, no cleanup needed.
  },
};
